import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

import Navbar from "./Navbar";
import Footer from "./Footer";
import { SITE_SETTINGS } from "src/constants/common";
import { SEO_DEFAULTS } from "src/constants/seo";

interface LayoutProps {
  children: React.ReactNode;
  meta?: {
    title?: string;
    description?: string;
    keywords?: string;
  };
}

const Layout: React.FC<LayoutProps> = ({ children, meta }) => {
  const location = useLocation();
  const {
    title = `${SITE_SETTINGS.NAME} — Inventory Management`,
    description = "Inventory Management System",
    keywords = "inventory, management, products, purchases, sales, suppliers",
  } = meta || {};

  const canonicalUrl = `${SEO_DEFAULTS.siteUrl}${location.pathname}`;
  const ogImageUrl = `${SEO_DEFAULTS.siteUrl}${SEO_DEFAULTS.ogImage}`;

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph tags for richer sharing */}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={SITE_SETTINGS.NAME} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImageUrl} />

        {/* Twitter card */}
        <meta name="twitter:card" content={SEO_DEFAULTS.twitterCard} />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={ogImageUrl} />
      </Helmet>

      <div className="font-display">
        <Navbar />
        <main className="min-h-[80vh]">{children}</main>
        <Footer />
      </div>
    </>
  );
};

export default Layout;
